import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { formatTimestamp } from '@/utils/formatting';
const columnHelper = createColumnHelper();
const columns = [
    columnHelper.accessor('address', {
        header: 'Address',
        cell: info => {
            const addr = info.getValue();
            const short = addr.length > 12 ? addr.slice(0, 6) + '...' + addr.slice(-4) : addr;
            return _jsx("span", { className: "font-mono text-primary text-[11px]", children: short });
        },
        enableSorting: false,
    }),
    columnHelper.accessor('sighting_count', {
        header: 'Sightings',
        cell: info => {
            const count = info.getValue();
            const color = count >= 5 ? 'text-success font-semibold' : count >= 3 ? 'text-warning' : 'text-slate-400';
            return _jsx("span", { className: color, children: count });
        },
    }),
    columnHelper.accessor('first_seen', {
        header: 'First Seen',
        cell: info => {
            const val = info.getValue();
            return _jsx("span", { className: "text-slate-400 text-[11px]", children: val ? formatTimestamp(val) : '--' });
        },
    }),
    columnHelper.accessor('last_seen', {
        header: 'Last Seen',
        cell: info => {
            const val = info.getValue();
            return _jsx("span", { className: "text-slate-400 text-[11px]", children: val ? formatTimestamp(val) : '--' });
        },
    }),
    columnHelper.accessor('markets_seen', {
        header: 'Markets',
        cell: info => {
            const markets = info.getValue();
            const count = markets ? markets.length : 0;
            return _jsx("span", { className: "text-slate-300", children: count });
        },
        sortingFn: (a, b) => (a.original.markets_seen?.length ?? 0) - (b.original.markets_seen?.length ?? 0),
    }),
    columnHelper.accessor('verified', {
        header: 'Status',
        cell: info => {
            const verified = info.getValue();
            const result = info.row.original.verification_result;
            if (verified) {
                return (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-success/20 text-success border border-success/20", children: "Verified" }));
            }
            if (result) {
                return (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-danger/20 text-danger border border-danger/20", children: result.length > 20 ? result.slice(0, 20) + '...' : result }));
            }
            return (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/20 text-slate-400 border border-slate-500/20", children: "Pending" }));
        },
        enableSorting: false,
    }),
];
export const DiscoveryQueue = React.memo(function DiscoveryQueue({ discovered, }) {
    const [sorting, setSorting] = useState([
        { id: 'sighting_count', desc: true },
    ]);
    const table = useReactTable({
        data: discovered,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (_jsxs("div", { className: "bg-surface-dark border border-surface-light rounded-xl overflow-hidden shadow-sm mb-6", children: [_jsx("div", { className: "p-5 border-b border-surface-light", children: _jsx("div", { className: "flex items-center justify-between", children: _jsxs("div", { children: [_jsxs("h3", { className: "font-bold text-white text-lg flex items-center gap-2", children: [_jsx("span", { className: "text-warning", children: "\uD83D\uDD0D" }), "Discovery Queue", _jsxs("span", { className: "text-slate-400 font-normal ml-1", children: ["(", discovered.length, ")"] })] }), _jsx("p", { className: "text-xs text-slate-400 mt-1", children: "Whales seen in trading but not yet on watchlist" })] }) }) }), discovered.length === 0 ? (_jsxs("div", { className: "text-center py-12 text-slate-500", children: [_jsx("p", { className: "text-lg mb-2", children: "\uD83D\uDD2D" }), _jsx("p", { children: "No discovered whales yet" })] })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-left text-sm", children: [_jsx("thead", { className: "bg-surface-dark text-slate-400 font-medium border-b border-surface-light", children: table.getHeaderGroups().map(headerGroup => (_jsx("tr", { children: headerGroup.headers.map(header => (_jsx("th", { className: `px-5 py-3 text-[10px] font-semibold uppercase tracking-wider ${header.column.getCanSort() ? 'cursor-pointer select-none hover:text-white' : ''}`, onClick: header.column.getToggleSortingHandler(), children: _jsxs("span", { className: "flex items-center gap-1", children: [flexRender(header.column.columnDef.header, header.getContext()), header.column.getIsSorted() === 'asc' && '↑', header.column.getIsSorted() === 'desc' && '↓'] }) }, header.id))) }, headerGroup.id))) }), _jsx("tbody", { className: "divide-y divide-surface-light text-slate-200", children: table.getRowModel().rows.map(row => (_jsx("tr", { className: "hover:bg-surface-light/30 transition-colors", children: row.getVisibleCells().map(cell => (_jsx("td", { className: "px-5 py-3 text-xs", children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id))) }, row.id))) })] }) }))] }));
});
//# sourceMappingURL=DiscoveryQueue.js.map